import { Injectable, inject } from '@angular/core';
import { CanActivate, CanActivateFn, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { switchMap, map } from 'rxjs/operators';
import { AclService } from './acl.service';
import { CookieService } from 'ngx-cookie-service';
import { UserService } from './services/user.service';
import { parseDdnSession } from './utils/ddn-session';
import { webddn } from 'src/environments/webddn';
import { content } from 'src/environments/data';


@Injectable({
  providedIn: 'root'
})
export class AclGuard implements CanActivate {
  app_type = webddn.APP_TYPE;

  constructor(private aclService: AclService, private router: Router, private cookieService: CookieService) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    // Restrict unapproved managers to /profile only
    const session = parseDdnSession(this.cookieService.get('ddnsession'));
    if (session?.token) {
      try {

        // Gate any pending (manual-approval) account. Auto-approved users have is_active:true,
        // so only user types configured as "manual" in Approval Config land here. Admins bypass.
        if (!session.is_active && session.role !== 's' && session.role !== 'a') {
          this.router.navigate(['/profile']);
          return false;
        }
        if ((session.role === 'b' || session.role === 't' && this.app_type !== 'sport') && session.has_schedule === false
            && state.url !== '/my-schedule' && state.url !== '/profile') {
          this.router.navigate(['/my-schedule']);
          return false;
        }
      } catch (e) {}
    }

    return this.authorizePath(route, state);
  }

  /**
   * Prefer exact URL route-binding (e.g. /billings/dashboard → feature.billing.payment).
   * Then use this snapshot's own routeConfig.data.requiredPermission — never inherited
   * parent data (Angular merges parent requiredPermission into route.data).
   */
  private authorizePath(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    const routePath = this.normalizePath(state.url);

    if (this.aclService.hasRouteBinding(routePath)) {
      if (this.aclService.hasRouteAccess(routePath)) {
        return true;
      }
      this.router.navigate(['/unauthorized']);
      return false;
    }

    const requiredPermission = this.ownRequiredPermission(route);
    if (requiredPermission) {
      if (this.aclService.hasPermission(requiredPermission)) {
        return true;
      }
      this.router.navigate(['/unauthorized']);
      return false;
    }

    return true;
  }

  /** Only the route's own config — ignores inherited parent data. */
  private ownRequiredPermission(route: ActivatedRouteSnapshot): string | undefined {
    const own = route.routeConfig?.data?.['requiredPermission'];
    return typeof own === 'string' && own.trim() ? own.trim() : undefined;
  }

  /**
   * Deepest child that declares its own requiredPermission, else deepest child.
   * Then authorize against the URL binding / that leaf's own permission.
   */
  canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    let leaf: ActivatedRouteSnapshot = route;
    let withOwnPerm: ActivatedRouteSnapshot | null = this.ownRequiredPermission(route)
      ? route
      : null;
    while (leaf.firstChild) {
      leaf = leaf.firstChild;
      if (this.ownRequiredPermission(leaf)) {
        withOwnPerm = leaf;
      }
    }
    return this.authorizePath(withOwnPerm || leaf, state);
  }

  private normalizePath(url: string): string {
    const path = (url || '').split('?')[0].split('#')[0];
    if (!path) {
      return '/';
    }
    return path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;
  }
}

/**
 * Modern Functional Route Guard for Angular 22
 */
export const aclGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const guard = inject(AclGuard);
  const aclService = inject(AclService);
  const userService = inject(UserService);
  return userService.ensureSessionRole().pipe(
    switchMap(() => aclService.whenEffectiveReady()),
    map(() => guard.canActivate(route, state))
  );
};

export const aclChildGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const guard = inject(AclGuard);
  const aclService = inject(AclService);
  const userService = inject(UserService);
  return userService.ensureSessionRole().pipe(
    switchMap(() => aclService.whenEffectiveReady()),
    map(() => guard.canActivateChild(route, state))
  );
};
